import { useState, useEffect, KeyboardEventHandler, ChangeEvent } from 'react'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Switch } from '../ui/switch'
import { InputGroup, NumericInput as BlueprintNumericInput } from '@blueprintjs/core'
import { HTMLSelect, OptionProps } from '../ui/html-select'
import React, { KeyboardEvent as ReactKeyboardEvent } from 'react'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'

interface InputProps {
    loading?: boolean
    disabled?: boolean
    label?: string
    placeholder?: string
    name?: string
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>
    className?: ClassValue
    style?: React.CSSProperties
    lh?: string | null
    leftIcon?: JSX.Element
    rightElement?: JSX.Element
}

interface TextInputProps extends InputProps {
    type: 'text' | 'email' | 'password'
    value?: string
    onChange: (v: string) => void
    labelClassName?: ClassValue
    initialValue?: string
}

interface NumberInputProps extends InputProps {
    type: 'percentage' | 'money' | 'number'
    value?: string
    onChange: (v: string) => void
    stepSize?: number
    minorStepSize?: number
    max?: number
    min?: number
    decimalPlaces?: number
    fakeRightElement?: boolean
}

interface SwitchInputProps extends InputProps {
    type: 'switch'
    value?: boolean
    onChange: (v: boolean) => void
}

interface CheckboxInputProps extends InputProps {
    type: 'checkbox'
    value?: boolean
    onChange: (v: boolean) => void
}

interface SelectInputProps extends InputProps {
    type: 'select'
    value?: string
    onChange: (v: string) => void
    options: (string | number | OptionProps)[]
}

type FormInputProps =
    | TextInputProps
    | NumberInputProps
    | SwitchInputProps
    | CheckboxInputProps
    | SelectInputProps

// interface FormInputProps {
//     loading: boolean,
//     name: string,
//     label: string,
//     type: FormInputType,
//     disabled: boolean,
//     value: string|number|boolean,
//     onChange: (v: string|number|boolean) => void,
//     placeholder?: string|number,
// }

/**
 * @param {object} props
 * @param {string} props.name
 * @param {function} props.onChange
 * @param {string} props.label
 * @param {string} props.type
 * @param {any} props.value
 * @param {function} props.onKeyDown
 * @param {any} props.initialValue
 * @param {object} props.style
 * @param {object} props.className
 * @param {boolean} props.disabled
 * @return {*}
 * @constructor
 */
export const FormInput = (props: FormInputProps) => {
    switch (props.type) {
        case 'email':
        case 'text':
        case 'password':
            return (
                <TextInput
                    initialValue={props.initialValue ? props.initialValue : props.value}
                    {...props}
                />
            )
        case 'checkbox':
            return <CheckboxInput {...props} />
        case 'switch':
            return <SwitchInput {...props} />
        case 'number':
        case 'money':
        case 'percentage':
            return <NumericInput {...props} type={props.type} />
        case 'select':
            return <SelectInput {...props} />
    }
}

const TextInput = ({
    disabled,
    name,
    leftIcon,
    onChange,
    label,
    type = 'text',
    onKeyDown,
    className,
    initialValue,
    labelClassName,
    lh = null,
}: TextInputProps) => {
    const [value, setValue] = useState(initialValue || '')
    useEffect(() => {
        setValue(initialValue || '')
    }, [initialValue])

    const change = (e: ChangeEvent<any>) => {
        setValue(e.target.value)
        if (onChange) {
            onChange(e.target.value)
        }
    }

    if (!lh) {
        lh = '16pt'
    }

    if (!labelClassName) {
        labelClassName = 'col-md-4 col-form-label text-md-right'
    }

    return (
        <div className="form-group align-items-center row">
            {!!label && (
                <label
                    htmlFor={name}
                    style={{ lineHeight: lh ?? undefined }}
                    className={classNames(labelClassName)}
                >
                    {label}
                </label>
            )}

            <div className={!!label ? 'col-md-6' : 'col-md-12'}>
                <InputGroup
                    id={name}
                    leftElement={leftIcon}
                    type={type}
                    name={name}
                    value={value}
                    onChange={change}
                    className={classNames(className)}
                    disabled={disabled}
                    required={false}
                    autoComplete="off"
                    onKeyDown={onKeyDown}
                />
            </div>
        </div>
    )
}

const CheckboxInput = ({
    style,
    value,
    name,
    onChange,
    label,
    disabled,
    onKeyDown,
    className,
}: CheckboxInputProps) => {
    const change = (e: ChangeEvent<any>) => {
        if (onChange) {
            onChange(e.target.checked)
        }
    }

    return (
        <div style={style} className={classNames('form-group align-items-center row', className)}>
            <div className="col-md-6 offset-md-4">
                <Checkbox
                    className="mb-0"
                    label={label}
                    name={name}
                    id={name}
                    checked={value}
                    onChange={change}
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                />
            </div>
        </div>
    )
}

const SwitchInput = ({
    value,
    name,
    onChange,
    label,
    disabled,
    onKeyDown,
    className,
    style,
}: SwitchInputProps) => {
    const change = (e: ChangeEvent<any>) => {
        if (onChange) {
            onChange(e.target.checked)
        }
    }
    return (
        <div style={style} className={classNames('form-group align-items-center row', className)}>
            <div className="col-md-6 offset-md-4">
                <Switch
                    className="mb-0"
                    label={label}
                    name={name}
                    id={name}
                    checked={value}
                    onChange={change}
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                />
            </div>
        </div>
    )
}

const NumericInput = ({
    value,
    disabled,
    onChange,
    name,
    label,
    leftIcon,
    rightElement,
    stepSize,
    minorStepSize,
    max,
    min,
    type,
    decimalPlaces = 2,
    lh = null,
    fakeRightElement = false,
}: NumberInputProps) => {
    if (type === 'money') {
        leftIcon = <Button minimal>{window.marketplace.currency_symbol}</Button>
        stepSize = 0.01
        minorStepSize = undefined
    } else if (type === 'percentage') {
        rightElement = <Button minimal>%</Button>
        stepSize = 1 / Math.pow(10, decimalPlaces)
        minorStepSize = undefined
    }

    // use this to make all the inputs have same input width
    if (fakeRightElement) {
        rightElement = <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
    }

    if (!lh) {
        lh = '16pt'
    }

    return (
        <div className="form-group align-items-center row">
            {!!label && (
                <label
                    htmlFor={name}
                    style={{ lineHeight: lh }}
                    className="col-md-4 col-form-label text-md-right"
                >
                    {label}
                </label>
            )}

            <div className={!!label ? 'col-md-6' : 'col-md-12'}>
                <BlueprintNumericInput
                    max={max}
                    min={min}
                    name={name}
                    disabled={disabled}
                    value={value}
                    leftIcon={leftIcon}
                    rightElement={rightElement}
                    onValueChange={(valueAsNumber, valueAsString) => {
                        // Handle the case where user types .02, -0.02, 0.02, -, etc.
                        // BlueprintJS sometimes has issues with these patterns
                        if (valueAsString === '' || valueAsString === undefined) {
                            onChange('')
                        } else if (
                            valueAsString === '-' ||
                            valueAsString === '.' ||
                            valueAsString === '-.'
                        ) {
                            // Allow partial input states for negative numbers and decimals
                            onChange(valueAsString)
                        } else if (isNaN(valueAsNumber)) {
                            // If BlueprintJS can't parse it but it looks like a valid partial number, keep it
                            if (/^-?\d*\.?\d*$/.test(valueAsString)) {
                                onChange(valueAsString)
                            } else {
                                onChange('')
                            }
                        } else {
                            onChange(valueAsString)
                        }
                    }}
                    stepSize={stepSize}
                    minorStepSize={minorStepSize}
                    allowNumericCharactersOnly={false}
                    selectAllOnFocus={true}
                />
            </div>
        </div>
    )
}

const SelectInput = ({
    disabled,
    value,
    options,
    name,
    onChange,
    label,
    className,
}: SelectInputProps) => {
    const change = (e: ChangeEvent<any>) => {
        if (onChange) {
            onChange(e.target.value)
        }
    }

    return (
        <div className={classNames('form-group align-items-center row', className)}>
            {!!label && (
                <label
                    htmlFor={name}
                    className="col-md-4 col-form-label text-md-right whitespace-nowrap"
                >
                    {label}
                </label>
            )}

            <div className={!!label ? 'col-md-6' : 'col-md-12'}>
                <HTMLSelect
                    disabled={disabled}
                    value={value || ''}
                    options={options}
                    onChange={change}
                />
            </div>
        </div>
    )
}

/**
 * Returns a function that can be passed to the onKeyDown event.
 *    onKeyDown={this.onKeyDown}
 *
 * When a keydown event happens and it is the enter key, the
 * callback will be called
 *
 * @param {any} callback
 * @return {any}
 */
export const useEnterSubmit = (callback?: () => void) => {
    return (e: KeyboardEvent | ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            e.stopPropagation()

            callback ? callback() : null
        }
    }
}
